import React, { useContext, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ConfigContext } from "../../Context/ConfigContext";
import axios from "axios";
import ReactECharts from 'echarts-for-react';

const Dashboard = () => {
  const { apiURL, apiHeaderJson, imageURL, user_profile_id } = useContext(ConfigContext);

  const [loading, setLoading] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [project, setProject] = useState(null);
  const [projectItems, setProjectItems] = useState([]);
  const [projectRequests, setProjectRequests] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [projectGallery, setProjectGallery] = useState([]);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const marqueeRef = useRef(null);

  const getDashboardData = async () => {
    try {
      setLoading(true);
      const headers = apiHeaderJson;
      const response = await axios.get(`${apiURL}api/GetDashboardForClient`, {
        headers,
        params: { client_id: user_profile_id }
      });
      const { success, data } = response.data;

      if (success) {
        setClientInfo(data?.client_info);
        const allProjects = data?.tabs?.total_projects || [];
        const currentProject = allProjects.length > 0 ? allProjects[0] : null;
        setProject(currentProject);
        setProjectItems(currentProject?.project_items || []);
        setProjectRequests(data?.tabs?.projects_requests || []);
        setProjectTasks(currentProject?.project_tasks || []);
        setProjectGallery(currentProject?.project_gallary || []);
      } else {
        setClientInfo(null);
        setProject(null);
        setProjectItems([]);
        setProjectRequests([]);
        setProjectTasks([]);
        setProjectGallery([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, [user_profile_id]);

  const getProjectStatusLabel = (status) => {
    switch (Number(status)) {
      case 0: return "Active";
      case 1: return "In-Active";
      case 2: return "Completed";
      default: return "Unknown";
    }
  };

  const getTaskPriorityLabel = (priority) => {
    switch (Number(priority)) {
      case 1: return { label: "Low", color: "success" };
      case 2: return { label: "Medium", color: "warning" };
      case 3: return { label: "High", color: "danger" };
      default: return { label: "Normal", color: "secondary" };
    }
  };

  const getTaskStatusLabel = (status) => {
    switch (Number(status)) {
      case 0: return { label: "Pending", color: "warning" };
      case 1: return { label: "In Progress", color: "info" };
      case 2: return { label: "Completed", color: "success" };
      default: return { label: "Unknown", color: "secondary" };
    }
  };

  const getItemChartOption = () => {
    const itemNames = projectItems.map(item => item.item_name || 'Unknown');
    const qtyData = projectItems.map(item => item.pi_item_qty || 0);
    const receivedData = projectItems.map(item => item.pi_item_received_qty || 0);
    const remainingData = projectItems.map(item => (item.pi_item_qty || 0) - (item.pi_item_received_qty || 0));

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { data: ['Total Quantity', 'Received', 'Remaining'], bottom: 0, textStyle: { fontSize: 11 } },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
      xAxis: { type: 'category', data: itemNames, axisLabel: { rotate: 30, fontSize: 10 } },
      yAxis: { type: 'value', name: 'Quantity' },
      series: [
        { name: 'Total Quantity', type: 'bar', data: qtyData, itemStyle: { color: '#405189' }, barGap: '10%' },
        { name: 'Received', type: 'bar', data: receivedData, itemStyle: { color: '#0ab39c' } },
        { name: 'Remaining', type: 'bar', data: remainingData, itemStyle: { color: '#f7b84c' } }
      ]
    };
  };

  const totalQty = projectItems.reduce((sum, item) => sum + Number(item.pi_item_qty || 0), 0);
  const totalReceived = projectItems.reduce((sum, item) => sum + Number(item.pi_item_received_qty || 0), 0);
  const completionPercentage = totalQty > 0 ? Math.min((totalReceived / totalQty) * 100, 100) : 0;
  const imageFiles = projectGallery.filter(item => item.pm_type === "IMAGE");

  // Duplicate the images so the marquee loops seamlessly (need at least 2 copies)
  const marqueeImages = imageFiles.length > 0
    ? (imageFiles.length < 5 ? [...imageFiles, ...imageFiles, ...imageFiles, ...imageFiles] : [...imageFiles, ...imageFiles])
    : [];

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      const currentIndex = imageFiles.findIndex(img => img.project_media_id === selectedImage.project_media_id);
      if (e.key === 'Escape') setSelectedImage(null);
      if (e.key === 'ArrowRight' && currentIndex < imageFiles.length - 1) {
        setSelectedImage(imageFiles[currentIndex + 1]);
      }
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setSelectedImage(imageFiles[currentIndex - 1]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, imageFiles]);

  if (loading) {
    return (
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      {/* ===== Gallery Custom Styles ===== */}
      <style>{`
        .premium-gallery {
          position: relative;
          background: linear-gradient(135deg, #ffffff 0%, #fafbff 50%, #f6fdfa 100%);
        }

        .premium-gallery::before {
          content: '';
          position: absolute;
          top: -80px;
          right: -80px;
          width: 260px;
          height: 260px;
          background: radial-gradient(circle, rgba(64, 81, 137, 0.10) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
          animation: auraFloat 8s ease-in-out infinite;
        }

        .premium-gallery::after {
          content: '';
          position: absolute;
          bottom: -80px;
          left: -80px;
          width: 260px;
          height: 260px;
          background: radial-gradient(circle, rgba(10, 179, 156, 0.10) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
          animation: auraFloat 8s ease-in-out infinite reverse;
        }

        @keyframes auraFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-15px, 15px) scale(1.1); }
        }

        /* Animated accent bar */
        .gallery-header-accent {
          position: relative;
          padding-left: 14px;
        }

        .gallery-header-accent::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 22px;
          border-radius: 4px;
          background: linear-gradient(180deg, #405189 0%, #0ab39c 100%);
          animation: galleryAccentPulse 2.5s ease-in-out infinite;
        }

        @keyframes galleryAccentPulse {
          0%, 100% { height: 22px; opacity: 1; }
          50% { height: 30px; opacity: 0.6; }
        }

        /* ===== Marquee Container ===== */
        .marquee-wrapper {
          position: relative;
          overflow: hidden;
          padding: 12px 0 20px;
          mask-image: linear-gradient(
            to right,
            transparent 0%,
            black 6%,
            black 94%,
            transparent 100%
          );
          -webkit-mask-image: linear-gradient(
            to right,
            transparent 0%,
            black 6%,
            black 94%,
            transparent 100%
          );
        }

        .marquee-track {
          display: flex;
          gap: 20px;
          width: max-content;
          animation: marqueeScroll 60s linear infinite;
          will-change: transform;
        }

        .marquee-wrapper:hover .marquee-track,
        .marquee-wrapper.paused .marquee-track {
          animation-play-state: paused;
        }

        @keyframes marqueeScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        /* Individual gallery card */
        .gallery-card-marquee {
          flex: 0 0 auto;
          width: 290px;
          height: 220px;
          border-radius: 20px;
          overflow: hidden;
          position: relative;
          cursor: pointer;
          background: #fff;
          box-shadow: 0 10px 30px rgba(64, 81, 137, 0.10);
          transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1),
                      box-shadow 0.5s cubic-bezier(0.22, 1, 0.36, 1);
          transform-style: preserve-3d;
        }

        .gallery-card-marquee:hover {
          transform: translateY(-10px) scale(1.04);
          box-shadow: 0 24px 50px rgba(64, 81, 137, 0.28);
        }

        /* Animated gradient border on hover */
        .gallery-card-marquee::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 20px;
          padding: 2px;
          background: linear-gradient(135deg, #405189, #0ab39c, #f7b84c, #405189);
          background-size: 300% 300%;
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 0.4s ease;
          animation: gradientRotate 3s linear infinite;
          pointer-events: none;
          z-index: 5;
        }

        .gallery-card-marquee:hover::before {
          opacity: 1;
        }

        @keyframes gradientRotate {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .gallery-card-marquee img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.8s cubic-bezier(0.22, 1, 0.36, 1), filter 0.5s ease;
          display: block;
        }

        .gallery-card-marquee:hover img {
          transform: scale(1.15) rotate(1.5deg);
          filter: brightness(1.06) saturate(1.2);
        }

        /* Shine sweep */
        .gallery-card-marquee::after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 60%;
          height: 100%;
          background: linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          transform: skewX(-25deg);
          z-index: 3;
          pointer-events: none;
        }

        .gallery-card-marquee:hover::after {
          animation: shineSweep 0.9s ease forwards;
        }

        @keyframes shineSweep {
          100% { left: 150%; }
        }

        /* Overlay */
        .gallery-overlay-marquee {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.35) 45%, transparent 100%);
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 16px;
          opacity: 0.9;
          transition: opacity 0.4s ease;
          z-index: 2;
        }

        .gallery-card-marquee:hover .gallery-overlay-marquee {
          opacity: 1;
        }

        .gallery-title-marquee {
          color: #fff;
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 4px;
          transform: translateY(10px);
          transition: all 0.4s ease 0.05s;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .gallery-subtitle-marquee {
          color: rgba(255, 255, 255, 0.78);
          font-size: 11.5px;
          transform: translateY(14px);
          opacity: 0;
          transition: all 0.4s ease 0.1s;
        }

        .gallery-card-marquee:hover .gallery-title-marquee,
        .gallery-card-marquee:hover .gallery-subtitle-marquee {
          transform: translateY(0);
          opacity: 1;
        }

        /* Eye button */
        .gallery-eye-btn-marquee {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          opacity: 0;
          transform: scale(0.5) rotate(-45deg);
          transition: all 0.45s cubic-bezier(0.22, 1, 0.36, 1);
          z-index: 4;
        }

        .gallery-card-marquee:hover .gallery-eye-btn-marquee {
          opacity: 1;
          transform: scale(1) rotate(0deg);
        }

        /* Index badge */
        .gallery-index-badge-marquee {
          position: absolute;
          top: 14px;
          left: 14px;
          background: rgba(0, 0, 0, 0.35);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          color: #fff;
          font-size: 11px;
          font-weight: 600;
          padding: 4px 11px;
          border-radius: 20px;
          z-index: 4;
          letter-spacing: 0.6px;
        }

        /* Live status pill */
        .gallery-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 5px 13px;
          border-radius: 30px;
          background: rgba(10, 179, 156, 0.1);
          color: #0ab39c;
          font-size: 11.5px;
          font-weight: 600;
          letter-spacing: 0.4px;
          border: 1px solid rgba(10, 179, 156, 0.2);
        }

        .gallery-status-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #0ab39c;
          box-shadow: 0 0 0 0 rgba(10, 179, 156, 0.7);
          animation: livePulse 1.8s infinite;
        }

        @keyframes livePulse {
          0% { box-shadow: 0 0 0 0 rgba(10, 179, 156, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(10, 179, 156, 0); }
          100% { box-shadow: 0 0 0 0 rgba(10, 179, 156, 0); }
        }

        .gallery-status-pill.paused-pill {
          background: rgba(247, 184, 76, 0.12);
          color: #d99a2b;
          border-color: rgba(247, 184, 76, 0.3);
        }

        .gallery-status-pill.paused-pill .gallery-status-dot {
          background: #f7b84c;
          animation: none;
          box-shadow: 0 0 0 0 rgba(247, 184, 76, 0.7);
        }

        /* Empty state */
        .gallery-empty {
          padding: 50px 20px;
          text-align: center;
          border: 2px dashed #e2e6ef;
          border-radius: 20px;
          background: #fafbfe;
          position: relative;
          overflow: hidden;
        }

        .gallery-empty::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 50% 50%, rgba(64, 81, 137, 0.04) 0%, transparent 70%);
          animation: emptyGlow 4s ease-in-out infinite;
        }

        @keyframes emptyGlow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }

        .gallery-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #eef1f9, #e2f7f3);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          animation: emptyFloat 3s ease-in-out infinite;
          position: relative;
          z-index: 1;
        }

        @keyframes emptyFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(3deg); }
        }

        /* ===== Lightbox ===== */
        .lightbox-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(15, 20, 40, 0.94);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: lightboxFadeIn 0.3s ease;
          padding: 20px;
        }

        @keyframes lightboxFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .lightbox-content {
          position: relative;
          max-width: 90vw;
          max-height: 85vh;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 40px 100px rgba(0, 0, 0, 0.7);
          animation: lightboxZoomIn 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }

        @keyframes lightboxZoomIn {
          from { opacity: 0; transform: scale(0.88); }
          to { opacity: 1; transform: scale(1); }
        }

        .lightbox-content img {
          display: block;
          max-width: 90vw;
          max-height: 85vh;
          object-fit: contain;
        }

        .lightbox-close {
          position: fixed;
          top: 24px;
          right: 24px;
          width: 46px;
          height: 46px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          cursor: pointer;
          transition: all 0.35s ease;
          z-index: 10001;
          backdrop-filter: blur(10px);
        }

        .lightbox-close:hover {
          background: rgba(255, 80, 80, 0.85);
          transform: rotate(90deg) scale(1.1);
          border-color: transparent;
        }

        .lightbox-nav {
          position: fixed;
          top: 50%;
          transform: translateY(-50%);
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10001;
          backdrop-filter: blur(10px);
        }

        .lightbox-nav:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-50%) scale(1.14);
        }

        .lightbox-nav.prev { left: 24px; }
        .lightbox-nav.next { right: 24px; }

        .lightbox-caption {
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(12px);
          color: #fff;
          padding: 10px 24px;
          border-radius: 30px;
          font-size: 13px;
          border: 1px solid rgba(255, 255, 255, 0.15);
          z-index: 10001;
          max-width: 80vw;
          text-align: center;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        @media (max-width: 576px) {
          .gallery-card-marquee { width: 240px; height: 185px; }
          .marquee-track { animation-duration: 40s; gap: 14px; }
          .lightbox-nav { width: 42px; height: 42px; font-size: 20px; }
          .lightbox-nav.prev { left: 10px; }
          .lightbox-nav.next { right: 10px; }
        }
      `}</style>

      <div className="page-content">
        <div className="container-fluid">
          {/* Header */}
          <div className="row mb-3 pb-1">
            <div className="col-lg-12">
              <div className="d-flex align-items-lg-center flex-lg-row flex-column">
                <div className="flex-grow-1">
                  <h4 className="fs-16 mb-1">Welcome back, {clientInfo?.profile_name || 'Client'}!</h4>
                  <p className="text-muted mb-0">
                    {project ? `Project: ${project.project_title}` : 'No active project found.'}
                  </p>
                </div>
                <div className="mt-3 mt-lg-0">
                  {project && (
                    <Link to={`/ProjectProfile/${project.project_id}`} className="btn btn-soft-info btn-sm">
                      <i className="ri-eye-line align-middle me-1" />View Project Details
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {project ? (
            <>
              {/* ===== INFINITE AUTO-SCROLL GALLERY ===== */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm overflow-hidden premium-gallery">
                    <div className="card-body p-4">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                        <div>
                          <h4 className="card-title mb-1 fw-semibold gallery-header-accent">
                            <i className="ri-image-2-line me-2 text-primary"></i>
                            Project Gallery
                          </h4>
                          <p className="text-muted mb-0 fs-13" style={{ paddingLeft: '14px' }}>
                            Visual journey of your project
                          </p>
                        </div>

                        {/* {imageFiles.length > 0 && (
                          <div
                            className={`gallery-status-pill ${isPaused ? 'paused-pill' : ''}`}
                            onMouseEnter={() => setIsPaused(true)}
                            onMouseLeave={() => setIsPaused(false)}
                          >
                            <span className="gallery-status-dot"></span>
                            {isPaused ? 'PAUSED' : 'AUTO-PLAYING'}
                          </div>
                        )} */}
                      </div>

                      {imageFiles.length > 0 ? (
                        <div
                          className={`marquee-wrapper ${isPaused ? 'paused' : ''}`}
                          ref={marqueeRef}
                          onMouseEnter={() => setIsPaused(true)}
                          onMouseLeave={() => setIsPaused(false)}
                        >
                          <div className="marquee-track">
                            {marqueeImages.map((img, idx) => (
                              <div
                                className="gallery-card-marquee"
                                key={`${img.project_media_id || idx}-${idx}`}
                                onClick={() => setSelectedImage(img)}
                              >
                                {/* <span className="gallery-index-badge-marquee">
                                  {String((idx % imageFiles.length) + 1).padStart(2, '0')} / {String(imageFiles.length).padStart(2, '0')}
                                </span> */}

                                <div className="gallery-eye-btn-marquee">
                                  <i className="ri-eye-line"></i>
                                </div>

                                <img
                                  src={`${imageURL}${img.pm_file_name}`}
                                  alt={img.pm_title || "Gallery Image"}
                                  loading="lazy"
                                  onError={(e) => {
                                    e.target.src = "/assets/images/placeholder-image.jpg";
                                  }}
                                />

                                <div className="gallery-overlay-marquee">
                                  <div className="gallery-title-marquee">
                                    {img.pm_title || "Project Image"}
                                  </div>
                                  <div className="gallery-subtitle-marquee">
                                    <i className="ri-zoom-in-line me-1"></i>
                                    Click to preview
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="gallery-empty">
                          <div className="gallery-empty-icon">
                            <i className="ri-image-add-line fs-2 text-primary"></i>
                          </div>
                          <h6 className="mb-1 position-relative">No images yet</h6>
                          <p className="text-muted mb-0 fs-13 position-relative">
                            Project images will appear here once uploaded.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== Project Information (unchanged) ===== */}
              <div className="row">
                {/* Tasks */}
                <div className="col-xl-12">
                  <div className="card">
                    <div className="card-header align-items-center d-flex">
                      <h4 className="card-title mb-0 flex-grow-1">
                        <i className="ri-task-line me-1"></i> Project Tasks
                      </h4>
                    </div>
                    <div className="card-body p-0">
                      {projectTasks.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: '40px' }}></th>
                                <th>Task</th>
                                <th>Priority</th>
                                <th>Due Date</th>
                                <th>Status</th>
                                <th style={{ width: '40px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {projectTasks.map((task) => {
                                const priority = getTaskPriorityLabel(task.project_task_priority);
                                const taskStatus = getTaskStatusLabel(task.project_task_status);
                                const isExpanded = expandedTaskId === task.project_task_id;
                                return (
                                  <React.Fragment key={task.project_task_id}>
                                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedTaskId(isExpanded ? null : task.project_task_id)}>
                                      <td>
                                        <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line fs-5`}></i>
                                      </td>
                                      <td>
                                        <div className="fw-medium">{task.project_task_title}</div>
                                        <small className="text-muted">{task.project_task_description?.substring(0, 50)}...</small>
                                      </td>
                                      <td>
                                        <span className={`badge bg-${priority.color}-subtle text-${priority.color}`}>{priority.label}</span>
                                      </td>
                                      <td className="text-nowrap">
                                        <small>{new Date(task.project_task_due_datetime).toLocaleDateString()}</small>
                                      </td>
                                      <td>
                                        <span className={`badge bg-${taskStatus.color}-subtle text-${taskStatus.color}`}>{taskStatus.label}</span>
                                      </td>
                                      <td>
                                        {task.comments?.length > 0 && (
                                          <span className="badge bg-light text-dark">
                                            <i className="ri-chat-3-line me-1"></i>{task.comments.length}
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                    {isExpanded && (
                                      <tr>
                                        <td colSpan="6" className="p-0">
                                          <div className="p-3 bg-light">
                                            <div className="">
                                              <h6 className="mb-2 fs-13">
                                                <i className="ri-chat-3-line me-1"></i> Comments ({task.comments?.length || 0})
                                              </h6>
                                              {task.comments && task.comments.length > 0 ? (
                                                <div className="comments-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                  {task.comments.slice().reverse().map((comment, idx) => (
                                                    <div className="d-flex mb-2" key={comment.pt_comment_id || idx}>
                                                      <div className="flex-shrink-0">
                                                        <div className="avatar-xs">
                                                          <div className="avatar-title bg-white rounded-circle">
                                                            <i className="ri-user-3-line text-primary fs-10"></i>
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex-grow-1 ms-2">
                                                        <div className="bg-white p-2 rounded">
                                                          <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <strong className="fs-11">{comment.comment_created_by || 'User'}</strong>
                                                            <small className="text-muted fs-11">
                                                              {new Date(comment.pt_comment_datetime).toLocaleString()}
                                                            </small>
                                                          </div>
                                                          <p className="mb-0 fs-12">{comment.pt_comment}</p>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>
                                              ) : (
                                                <p className="text-muted text-center py-2 mb-0 fs-12">No comments yet.</p>
                                              )}
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <i className="ri-task-line fs-1 text-muted"></i>
                          <p className="text-muted mb-0 mt-2">No tasks assigned yet.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div className="row mt-3">
                <div className="col-xl-12">
                  <div className="card">
                    <div className="card-header align-items-center d-flex">
                      <h4 className="card-title mb-0 flex-grow-1">Item Quantity Overview</h4>
                    </div>
                    <div className="card-body">
                      <ReactECharts option={getItemChartOption()} style={{ height: '400px' }} />
                    </div>
                  </div>
                </div>
                
              </div>
            </>
          ) : (
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center py-5">
                    <div className="avatar-md mx-auto mb-3">
                      <div className="avatar-title bg-light rounded-circle fs-2">
                        <i className="ri-building-line text-muted" />
                      </div>
                    </div>
                    <h5>No Project Found</h5>
                    <p className="text-muted">You don't have any active projects at the moment.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== Lightbox Modal ===== */}
      {selectedImage && (
        <div className="lightbox-backdrop" onClick={() => setSelectedImage(null)}>
          <div className="lightbox-close" onClick={() => setSelectedImage(null)}>
            <i className="ri-close-line"></i>
          </div>

          {(() => {
            const currentIndex = imageFiles.findIndex(
              (img) => img.project_media_id === selectedImage.project_media_id
            );
            return (
              <>
                {currentIndex > 0 && (
                  <div
                    className="lightbox-nav prev"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(imageFiles[currentIndex - 1]);
                    }}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </div>
                )}

                {currentIndex < imageFiles.length - 1 && (
                  <div
                    className="lightbox-nav next"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(imageFiles[currentIndex + 1]);
                    }}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </div>
                )}

                <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                  <img
                    src={`${imageURL}${selectedImage.pm_file_name}`}
                    alt={selectedImage.pm_title || "Gallery Image"}
                    onError={(e) => {
                      e.target.src = "/assets/images/placeholder-image.jpg";
                    }}
                  />
                </div>

                <div className="lightbox-caption">
                  <i className="ri-image-line me-2"></i>
                  {selectedImage.pm_title || "Project Image"}
                  <span className="mx-2">•</span>
                  {currentIndex + 1} / {imageFiles.length}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default Dashboard;